import type { ComponentProps, FunctionComponent, ReactNode } from 'react';

import { useShakeOnClickOutside } from '../../animations/shake/use-shake-on-click-outside';
import { Button } from '../../core/button/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../core/popover/Popover';
import { Textarea, type TextareaProps } from '../../core/textarea/Textarea';

type PromptComposerActionButtonSize = 'default' | NonNullable<ComponentProps<typeof Button>['size']>;

type PromptComposerBaseProps = {
    readonly actionButtonSize?: PromptComposerActionButtonSize;
    readonly cancelLabel: ReactNode;
    readonly children: ReactNode;
    readonly isSubmitting?: boolean;
    readonly onCancel: () => void;
    readonly onSubmit: () => void;
    readonly submitDisabled?: boolean;
    readonly submitLabel: ReactNode;
    readonly submitTestId?: string;
};

type PromptComposerMode = 'inline' | 'popover' | 'rail';

const getComposerSpacing = (
    mode: PromptComposerMode,
): { contentGapClass: string; paddingClass: string; radiusClass: string } =>
    mode === 'inline'
        ? { contentGapClass: 'gap-l', paddingClass: 'p-0', radiusClass: '' }
        : {
              contentGapClass: mode === 'rail' ? 'gap-l' : 'gap-xl',
              paddingClass: 'p-xl',
              radiusClass: 'rounded-xxxl',
          };

type PromptComposerPopoverProps = PromptComposerBaseProps &
    Pick<ComponentProps<typeof PopoverContent>, 'align'> & {
        readonly isOpen: boolean;
        readonly mode: 'popover';
        readonly trigger: ReactNode;
        readonly width?: string;
    };

type PromptComposerRailProps = PromptComposerBaseProps & {
    readonly mode: Extract<PromptComposerMode, 'rail'>;
};

type PromptComposerInlineProps = PromptComposerBaseProps & {
    readonly mode: Extract<PromptComposerMode, 'inline'>;
};

type PromptComposerProps = PromptComposerPopoverProps | PromptComposerRailProps | PromptComposerInlineProps;

type PromptComposerInputProps = Omit<TextareaProps, 'className' | 'variant'> & {
    readonly actions?: ReactNode;
    readonly recordingOverlay?: ReactNode;
    readonly textareaClassName?: string;
};

export const PromptComposerInput: FunctionComponent<PromptComposerInputProps> = ({
    actions,
    recordingOverlay,
    textareaClassName = '',
    ...textareaProps
}) => {
    const isRecordingOverlayVisible = recordingOverlay !== undefined;

    return (
        <div className="relative w-full">
            <Textarea
                {...textareaProps}
                className={`block transition-opacity duration-200 ease ${actions === undefined ? '' : 'pb-8xl pr-8xl'} ${isRecordingOverlayVisible ? 'pointer-events-none opacity-0' : ''} ${textareaClassName}`}
            />
            {isRecordingOverlayVisible && (
                <div className="absolute inset-0 overflow-hidden rounded-md border border-gray-300 bg-white-200">
                    {recordingOverlay}
                </div>
            )}
            {actions !== undefined && !isRecordingOverlayVisible && (
                <div className="absolute bottom-s right-s flex items-center gap-s">{actions}</div>
            )}
        </div>
    );
};

type PromptComposerSurfaceProps = PromptComposerBaseProps & {
    readonly contentGapClass: string;
};

const PromptComposerSurface: FunctionComponent<PromptComposerSurfaceProps> = ({
    actionButtonSize = 'sm',
    cancelLabel,
    children,
    contentGapClass,
    isSubmitting = false,
    onCancel,
    onSubmit,
    submitDisabled = false,
    submitLabel,
    submitTestId,
}) => {
    const buttonSize: ComponentProps<typeof Button>['size'] =
        actionButtonSize === 'default' ? undefined : actionButtonSize;

    return (
        <div className={`flex w-full flex-col items-end ${contentGapClass}`}>
            {children}
            <div className="flex items-center justify-end gap-s">
                <Button size={buttonSize} variant="tertiary" onClick={onCancel}>
                    {cancelLabel}
                </Button>
                <Button
                    data-testid={submitTestId}
                    disabled={submitDisabled || isSubmitting}
                    size={buttonSize}
                    onClick={onSubmit}
                >
                    {submitLabel}
                </Button>
            </div>
        </div>
    );
};

export const PromptComposer: FunctionComponent<PromptComposerProps> = (props) => {
    const { isShaking, shake } = useShakeOnClickOutside();
    const { contentGapClass, paddingClass, radiusClass } = getComposerSpacing(props.mode);
    const shadowClass = props.mode === 'popover' ? 'shadow-lg' : 'shadow-md';

    const handlePointerDownOutside = (event: Event): void => {
        shake();
        event.preventDefault();
    };

    if (props.mode === 'rail') {
        return (
            <div
                className={`motion-safe:animate-fade-in flex flex-col items-end ${radiusClass} bg-gray-100 ${paddingClass} font-sans ${shadowClass}`}
            >
                <PromptComposerSurface {...props} contentGapClass={contentGapClass} />
            </div>
        );
    }

    if (props.mode === 'inline') {
        return <PromptComposerSurface {...props} contentGapClass={contentGapClass} />;
    }

    const { align = 'end', isOpen, trigger, ...surfaceProps } = props;
    const width = props.width ?? '350px';
    // Shake the popover surface itself on outside-click. When not shaking, suppress the Radix
    // entrance animation per side so it doesn't replay once the shake clears.
    const animationClass = isShaking
        ? '!animate-popover-shake'
        : 'data-[side=bottom]:!animate-none data-[side=left]:!animate-none data-[side=right]:!animate-none data-[side=top]:!animate-none';

    return (
        <Popover open={isOpen}>
            <PopoverTrigger asChild={true}>{trigger}</PopoverTrigger>
            <PopoverContent
                align={align}
                className={`flex flex-col items-end ${radiusClass} border-none bg-gray-100 ${paddingClass} ${shadowClass} ${animationClass}`}
                style={{ width }}
                onPointerDownOutside={handlePointerDownOutside}
            >
                <PromptComposerSurface {...surfaceProps} contentGapClass={contentGapClass} />
            </PopoverContent>
        </Popover>
    );
};
